import { impossible } from 'common/types/impossible';
import { money, formatMoney, type FormattedMoney } from 'common/types/money';
import type { Pricing } from 'packages/types';

type GenericPricingByUnit<UnitValue> = {
  fixed: UnitValue;
  perHour: UnitValue;
  perPerson: UnitValue;
};

type PricingByUnit = GenericPricingByUnit<number | null>;

type FormattedPricingByUnit = GenericPricingByUnit<string | null>;

export function packageSummaryPricePrecedence<UnitValue>(
  genericPricingByUnit: GenericPricingByUnit<UnitValue>,
  chooser: (value: UnitValue) => boolean
): UnitValue {
  if (chooser(genericPricingByUnit.perPerson)) {
    return genericPricingByUnit.perPerson;
  }
  if (chooser(genericPricingByUnit.perHour)) {
    return genericPricingByUnit.perHour;
  }
  return genericPricingByUnit.fixed;
}

export function packagePricesFromByUnit(
  packagePricings: Array<Pricing>
): FormattedPricingByUnit {
  if (packagePricings.length === 0) {
    return {
      fixed: null,
      perPerson: null,
      perHour: null
    };
  }
  const allPricingLocales = new Set(packagePricings.map(p => p.locale));
  if (allPricingLocales.size !== 1) {
    throw new Error(
      `All package pricing locales need to match, found: ${Array.from(allPricingLocales).join(',')}`
    );
  }
  const locale = Array.from(allPricingLocales)[0];

  const smallestPricing = smallestPricingByUnit(packagePricings);

  return {
    fixed:
      smallestPricing.fixed === null
        ? null
        : fixedPriceToText(formatMoney(money(locale, smallestPricing.fixed))),
    perPerson:
      smallestPricing.perPerson === null
        ? null
        : perPersonPriceToText(
            formatMoney(money(locale, smallestPricing.perPerson))
          ),
    perHour:
      smallestPricing.perHour === null
        ? null
        : perHourPriceToText(
            formatMoney(money(locale, smallestPricing.perHour))
          )
  };
}

export function packagePricesFrom(
  packagePricings: Array<Pricing>
): null | string {
  const textByUnit = packagePricesFromByUnit(packagePricings);
  return packageSummaryPricePrecedence(textByUnit, text => !!text);
}

function pricingByUnit(pricing: Pricing): PricingByUnit {
  let fixed: number | null = null;
  if (pricing.genericFixed === null) {
    fixed = pricing.rentFixed;
  } else if (pricing.rentFixed === null) {
    fixed = pricing.genericFixed;
  } else {
    // Here both fixed fees are numbers or TypeScript would complain
    fixed = pricing.rentFixed + pricing.genericFixed;
  }

  return {
    fixed: fixed,
    perPerson: pricing.perPerson,
    perHour: pricing.rentPerHour
  };
}

type Order = 'LESS_THAN' | 'GREATER_THAN' | 'EQUAL';

// Take great care in touching this. Because of the indeterminacy introduced by
// null the semantics you get might not be what you want. Preferably, create
// a test for the property you'd like to achieve first!
function compareMaybeNumbers(left: number | null, right: number | null): Order {
  if (left === right) {
    return 'EQUAL';
  }
  // In this comparison semantic, null is assumed to be an
  // "indeterminately large quantity" and is greater than anything.
  if (left === null) {
    return 'GREATER_THAN';
  }
  if (right === null) {
    return 'LESS_THAN';
  }
  if (left < right) {
    return 'LESS_THAN';
  }
  if (left > right) {
    return 'GREATER_THAN';
  }
  return 'EQUAL';
}

function compare(
  leftPricing: PricingByUnit,
  rightPricing: PricingByUnit
): Order {
  const results = {
    fixed: compareMaybeNumbers(leftPricing.fixed, rightPricing.fixed),
    perHour: compareMaybeNumbers(leftPricing.perHour, rightPricing.perHour),
    perPerson: compareMaybeNumbers(
      leftPricing.perPerson,
      rightPricing.perPerson
    )
  };

  return packageSummaryPricePrecedence(results, unit => unit !== 'EQUAL');
}

function smallestPricingByUnit(packagePricings: Array<Pricing>): PricingByUnit {
  const emptyPricingByUnit: PricingByUnit = {
    fixed: null,
    perPerson: null,
    perHour: null
  };

  return packagePricings
    .map(pricingByUnit)
    .reduce((smallestSoFar: PricingByUnit, nextPricing: PricingByUnit) => {
      const comparisonResult = compare(smallestSoFar, nextPricing);
      switch (comparisonResult) {
        case 'EQUAL':
          return smallestSoFar;
        case 'GREATER_THAN':
          return nextPricing;
        case 'LESS_THAN':
          return smallestSoFar;
        default:
          return impossible(comparisonResult);
      }
    }, emptyPricingByUnit);
}

function fixedPriceToText(fixed: FormattedMoney): string {
  return fixed;
}

function perPersonPriceToText(perPerson: FormattedMoney): string {
  return I18n.t('packages.summary.pricing.per_person', {
    perPerson: perPerson
  });
}

function perHourPriceToText(perHour: FormattedMoney): string {
  return I18n.t('packages.summary.pricing.per_hour', {
    perHour: perHour
  });
}
